import {
  AutoMarkComponent,
  InteractiveComponent,
  LessonComponentBase,
  LessonComponentResponseType,
  NonAutoMarkComponent,
  SelectionComponent
} from '../models/LessonComponent';
import {
  LessonComponentContextModel,
  LessonComponentContextProvider
} from '../../content/models/LessonComponentContext';
import React, { useEffect, useState } from 'react';
import { isEmpty, isFunction, isNil } from 'lodash';

import { ApiPortfolioState } from '../../dashboard/models/ApiPortfolioState';
import AuthService from '../../shared/services/AuthService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { Disable } from 'react-disable';
import FeatureAccessService from '../../shared/services/FeatureAccessService';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import { IconButtonWithConfirmation } from '../../shared/components/IconButtonWithConfirmation';
import { LessonComponentContentView } from './LessonComponentContentView';
import { LessonComponentResultPanel } from './LessonComponentResultPanel';
import LessonComponentService from '../services/LessonComponentService';
import { LessonComponentStyle } from 'content/models/LessonComponentStyle';
import { LessonComponentSubmissionPanel } from './LessonComponentSubmissionPanel';
import { LessonComponentType } from '../models/LessonComponentType';
import { LessonPointsField } from './LessonPointsField';
import LessonResponseService from '../services/LessonResponseService';
import LockIcon from '@mui/icons-material/Lock';
import { NonAutoMarkingSubmissionPanel } from './NonAutoMarkingSubmissionPanel';
import SettingsIcon from '@mui/icons-material/Settings';
import StarIcon from '@mui/icons-material/Star';
import { StyleFormatter } from '../services/StyleService';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import styles from '../styles/LessonComponentView.scss';
import { useLessonContext } from '../../content/models/LessonContext';
import { useLessonEditContext } from '../models/LessonEditContext';
import { useLessonPlayerContext } from '../../dashboard/models/LessonPlayerContext';
import { useLessonPlayerMarkingContext } from '../../assessments/models/LessonPlayerMarkingContext';
import { usePortfolio } from '../../dashboard/hooks/usePortfolio';

export interface LessonComponentViewProps {
  locked: boolean; //locked determines whether it is required component
  component: LessonComponentBase;
  editComponent?: LessonComponentBase; //* not required in 'edit' mode
  editing: boolean; //editing used in the QuillEditor
  onSetup: (component: LessonComponentBase) => void;
  onRequired: (componentkey: number, required: boolean) => void;
  onDuplicate: () => void;
  onDelete: (componentkey: number) => void;
  onSelected: (componentkey: number, selected: boolean) => void;
  onResponse: (
    required: boolean,
    position: number,
    response: string,
    type?: LessonComponentType,
    points?: number,
    correct?: boolean
  ) => void;
  onBeforeClick: (componentKey: number) => void;
  onChangeAnswer?: (componentKey: number, userAnswer: string) => void;
  onUpdateMark?: (componentkey: number, mark: number) => void; //* used only in 'mark' display mode
}

export function LessonComponentView(props: LessonComponentViewProps) {
  const {
    mode,
    lessonType,
    finalized,
    quizLocked,
    assessmentSubmitted,
    portfolioLocked
  } = useLessonContext();
  const { templateData, selectedComponentKeys } = useLessonEditContext();
  const [assignedPoints, setAssignedPoints] = useState<number>();
  const [correct, setCorrect] = useState<boolean | null>(null);
  const [fullMark, setFullMark] =
    useState<LessonComponentContextModel['fullMark']>(false); //* Used to determine whether the learner can edit their answer for Non-auto-marked components.
  const [points, setPoints] = useState<number | null>(0);
  const [reattempt, setReattempt] = useState(false);
  const { lessonState } = useLessonPlayerContext();
  const [answerSaved, setAnswerSaved] = useState<boolean>(false);
  const { markerUserkey } = useLessonPlayerMarkingContext();
  const [modifiedDate, setModifiedDate] = useState<Date>(null);
  const [select, setSelect] = useState(false);

  //Padding will be applied in the child level for the components that has stretchBackground.
  const paddingStretch: LessonComponentStyle = {
    padding: props.component.style.padding
  };

  const stretch = {
    maxWidth: `${mode === 'edit' ? '676px' : '700px'}`, // Deduct drag icon width (24px) from its parent width (700px)
    ...StyleFormatter(paddingStretch)
  };

  const componentStyle: LessonComponentStyle = props.component.style
    .stretchBackground
    ? {
        ...props.component.style,
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0
        }
      }
    : props.component.style;

  const userkey = AuthService.Userkey;
  const { portfolio } =
    mode !== 'edit'
      ? usePortfolio(
          userkey,
          lessonState?.answerParams?.lessonkey,
          lessonState?.answerParams?.layer
        )
      : { portfolio: null };

  const isSurvey = (props.editComponent as InteractiveComponent)?.settings
    ?.survey;

  useEffect(() => {
    const interactiveComponent = props.component as InteractiveComponent;
    if (interactiveComponent) {
      setAssignedPoints(interactiveComponent.settings?.points || 0);
    }
    setSelect(false);
  }, [props.component]);

  useEffect(() => {
    if (props.component.answer) {
      setAnswerSaved(true);
      getCorrectAndPoints(props.component);
    }
  }, [props.component.answer]);

  /** saveTemplate() returns templateData as TemplateModel[]. */
  useEffect(() => {
    if (!isEmpty(templateData)) {
      setSelect(false);
    }
  }, [templateData]);

  const handleComponentSelection = () => {
    setSelect(!select);
    props.onSelected(props.component.componentkey, !select);
  };

  function handleSetup() {
    if (isFunction(props.onSetup)) {
      props.onSetup(props.component);
    }
  }

  function handleRequired() {
    if (isFunction(props.onRequired)) {
      props.onRequired(props.component.componentkey, !props.component.required);
    }
  }

  function handleDelete() {
    if (isFunction(props.onDelete)) {
      props.onDelete(props.component.componentkey);
    }
  }

  function handleChange(response: LessonComponentResponseType) {
    const responsePattern = LessonResponseService.getResponsePattern(
      props.component.type,
      response
    );

    if (isFunction(props.onChangeAnswer)) {
      props.onChangeAnswer(props.component.componentkey, responsePattern);
    }
  }

  function getCorrectAndPoints(
    propsComponent: AutoMarkComponent | NonAutoMarkComponent
  ) {
    let correctAnswer = null;
    if (propsComponent.settings.survey && propsComponent.answer) {
      correctAnswer = true;
    } else {
      correctAnswer = LessonResponseService.isResponseCorrect(
        propsComponent,
        (propsComponent as AutoMarkComponent)?.content.correctResponsePattern ||
          null,
        propsComponent.answer,
        (propsComponent as SelectionComponent)?.content.options || []
      );
    }
    const userPoints = LessonComponentService.isAutoMarking(propsComponent.type)
      ? correctAnswer
        ? propsComponent.settings?.points ?? 0
        : 0
      : isNil(propsComponent?.mark)
      ? null
      : propsComponent.mark;
    setCorrect(correctAnswer);
    setPoints(userPoints);
    const fullMark = !propsComponent.settings.survey
      ? userPoints === propsComponent.settings?.points
      : false;
    setFullMark(fullMark);
    return { correctAnswer, userPoints };
  }

  /**
   * This function gets called when the learner submits the answer using
   * 'save and continue' button for all components except video and carousel components
   */
  function handleSubmit() {
    setAnswerSaved(true);
    setReattempt(false);
    const correctResult = getCorrectAndPoints(props.editComponent);
    handleResponse(
      props.editComponent.answer,
      correctResult.userPoints,
      correctResult.correctAnswer
    );
  }

  /* For file uploader and carousel components there is no 'save and continue' button to save the answer.
  So the answer will be saved for video component when the video ends and for carousels, when the image renders */
  function handleSilentSubmit(userResponse: string) {
    // Don't save audio/video progress, carousels and hotspot answers for older version attempts
    if (!portfolioLocked) {
      handleChange(userResponse);
      handleResponse(userResponse);
    }
  }

  function handleResponse(
    userResponse: string,
    points: number | null = null,
    correct: boolean | null = null
  ) {
    if (mode !== 'mark') {
      setModifiedDate(dayjs().toDate());
    }
    props.onResponse(
      props.editComponent.required,
      props.editComponent.position,
      userResponse,
      props.editComponent.type,
      points,
      correct
    );
  }

  /** Used for file uploader component. Because there is no 'save and continue' for
   * file uploader, answerSave status needs to be set when file uploaded / removed.  */
  function handleAnswerSave(status: boolean) {
    if (!portfolioLocked) {
      setAnswerSaved(status);
      // Send fake answer as user answer in 'preview' mode to trigger the 'getLockPosition()' to be unset.
      if (mode === 'preview') {
        status ? handleResponse('preview') : handleResponse(null);
      }
    }
  }

  function getResponse() {
    /* If lesson is completed or quiz is finalized/locked, 
    then the saved component with response is passed to the child components*/
    const component =
      finalized || quizLocked ? props.component : props.editComponent;
    if (component) {
      return LessonResponseService.getResponseValue(component.type, component);
    }
  }

  function handleReattempt() {
    setPoints(null);
    setReattempt(true);
    setAnswerSaved(false);
  }

  function handlePointsChange(points: number) {
    //* In the 'mark' display mode only.
    //* A marker allocated points for a non-automarked component.
    props.onUpdateMark(props.editComponent.componentkey, points);
    handleResponse(
      props.editComponent.answer,
      points,
      !!props.editComponent.answer
      //* This is for non-automarked components,
      //* so the 'correct' value shows if there's any answer.
    );
  }

  /* In the 'work required' state, the points and feedback will be shown to the learners. 
  Once they re-answer the question, the feedback and points will be hidden. 
  So checking the 'answerModified' date for that particular component against the 'portfolio submitted date'. 
  Setting 'modifiedDate' as current dateTime when the answer is saved(handleResponse) to avoid flickering/showing the points for the modified answer.
  This happens because of the round trip to server and react re-renders the updated value. Anyway, the answerModified date will be overwritten in the API.
  */
  const isAnswerModified =
    portfolio &&
    (dayjs(props.component.answerModified).toDate() > portfolio.modified ||
      modifiedDate > portfolio.modified);

  //Show feedback whether the answer is correct or incorrect depending on the lesson type, lesson state and mode
  const showFeedback =
    lessonType === 'lesson' ||
    (lessonType === 'quiz' && (finalized || quizLocked)) ||
    (lessonType === 'assessment' &&
      (portfolio?.state === ApiPortfolioState.AssessmentLock ||
        (portfolio?.state === ApiPortfolioState.AssessmentRetry &&
          !isAnswerModified)));

  return (
    <div className={props.locked ? styles.lockedComponentContainer : ''}>
      {props.locked && (
        <div className={styles.centered}>
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            <Grid>
              <LockIcon />
            </Grid>
            <Grid>
              <Typography variant="caption">
                The above content must be completed first.
              </Typography>
            </Grid>
          </Grid>
        </div>
      )}
      <LessonComponentContextProvider
        value={{
          quillEditing: props.editing,
          reattempt,
          correct,
          isAnswerModified,
          fullMark,
          componentType: props.component?.type,
          userAnswer: props.component?.answer,
          showFeedback,
          answerSaved,
          isSurvey: (props.component as InteractiveComponent)?.settings?.survey,
          handleAnswerSave,
          handleReattempt,
          handleSubmit,
          onBeforeClick: props.onBeforeClick
        }}
      >
        <Grid
          container
          justifyContent="center"
          className={props.locked ? styles.locked : styles.unlocked}
          /* Applying the component settings style here */
          style={{
            flex: 1,
            ...StyleFormatter({
              ...componentStyle
            }),
            // setting overflow as hidden to show the element corner as rounded when the component has rounded corners
            // and setting as visible when editing the text. This will make the toolbar to be visible.
            overflow: props.editing ? 'visible' : 'hidden'
          }}
          direction="column"
          alignItems="center"
        >
          <Grid
            wrap="nowrap"
            sx={{
              width: '100%',
              ...(props.component.style?.stretchBackground
                ? stretch
                : undefined)
            }}
          >
            <Grid
              id={props.component?.componentkey?.toString()}
              style={{
                width: '100%'
              }}
              className={
                props.component.type === 'table' && mode === 'edit'
                  ? styles.tableComponent
                  : ''
              }
            >
              <Disable
                // Disable each component if lesson component is locked
                disabled={LessonComponentService.isLessonComponentLocked(
                  props.component,
                  mode,
                  correct,
                  reattempt,
                  finalized,
                  quizLocked,
                  lessonType,
                  assessmentSubmitted,
                  portfolioLocked
                )}
                disabledOpacity={1}
              >
                <LessonComponentContentView
                  component={
                    finalized || quizLocked || mode === 'edit'
                      ? props.component
                      : props.editComponent
                  }
                  response={getResponse()}
                  onChange={handleChange}
                  onSubmit={handleSilentSubmit} //* for components that submit a response directly from it (e.g., Video, Carousel, Hotspot)
                />
              </Disable>
            </Grid>
            {mode === 'edit' && (
              <Grid xs={1} style={{ paddingLeft: '8px' }}>
                <Grid container direction="column">
                  {FeatureAccessService.hasAuthorAccess && (
                    <Grid>
                      <IconButton
                        size="small"
                        color={select ? 'primary' : 'default'}
                        disabled={props.component.componentkey <= 0}
                        onClick={handleComponentSelection}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    </Grid>
                  )}
                  {LessonComponentService.hasAnswer(props.component) && (
                    <Grid>
                      <Tooltip title="Required?" arrow>
                        <IconButton
                          size="small"
                          onClick={handleRequired}
                          color={
                            props.component.required ? 'primary' : 'default'
                          }
                        >
                          <StarIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                  <Grid>
                    <IconButton size="small" onClick={handleSetup}>
                      <SettingsIcon />
                    </IconButton>
                  </Grid>
                  <Grid>
                    <IconButton size="small" onClick={props.onDuplicate}>
                      <FileCopyIcon />
                    </IconButton>
                  </Grid>
                  <Grid>
                    <IconButtonWithConfirmation
                      message={
                        selectedComponentKeys.includes(
                          props.component.componentkey
                        ) &&
                        `Are you sure you want to delete the ${selectedComponentKeys.length} selected components?`
                      }
                      size="small"
                      selected={select}
                      onConfirm={handleDelete}
                    >
                      <DeleteIcon />
                    </IconButtonWithConfirmation>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
          {
            // Allocate graded points in the Formal Assessments
            LessonComponentService.isInteractive(props.component) &&
              !!assignedPoints &&
              mode === 'mark' &&
              markerUserkey && (
                <Grid className={styles.points}>
                  <>
                    {LessonComponentService.isNonAutoMarking(
                      props.component
                    ) && (
                      <Grid className={styles.markerPoints}>
                        <LessonPointsField
                          points={points}
                          maxPoints={assignedPoints}
                          onPointsChange={handlePointsChange}
                        />
                      </Grid>
                    )}
                    <LessonComponentResultPanel
                      correct={correct}
                      points={points}
                      assignedPoints={assignedPoints}
                      message={
                        isNil(props.component.answer) &&
                        `${
                          props.component.type === 'file_uploader'
                            ? 'No file uploaded'
                            : 'Question not answered'
                        }`
                      }
                    />
                  </>
                </Grid>
              )
          }
          {
            // Graded points in the Formal Assessments
            mode === 'learn' &&
              (portfolio?.state === ApiPortfolioState.AssessmentLock ||
                (portfolio?.state === ApiPortfolioState.AssessmentRetry &&
                  !isAnswerModified)) &&
              LessonComponentService.isInteractive(props.component) &&
              !LessonComponentService.isAutoMarking(props.component.type) &&
              (props.component as InteractiveComponent).settings.points > 0 &&
              props.component.answer &&
              !reattempt && (
                <Grid className={styles.points}>
                  <Typography variant="body2" align="center">
                    {points === null
                      ? 'You are yet to be awarded any points'
                      : `You have been awarded ${points} out of ${assignedPoints} ${
                          assignedPoints === 1 ? 'point' : 'points'
                        }.`}
                  </Typography>
                </Grid>
              )
          }
          <Disable
            //Disable other buttons in the submission panel if lesson locked
            disabled={LessonComponentService.isLessonLocked(
              mode,
              finalized,
              quizLocked,
              lessonType,
              assessmentSubmitted,
              portfolioLocked
            )}
            disabledOpacity={1}
          >
            {LessonComponentService.canSubmit(mode) &&
              LessonComponentService.isAutoMarking(props.component.type) &&
              /* All survey question components will render NonAutoMarkingSubmissionPanel.
                 This is because to show 'Edit Answer' when reattempting */
              !isSurvey && (
                <LessonComponentSubmissionPanel
                  points={points}
                  assignedPoints={assignedPoints}
                  component={
                    finalized || quizLocked
                      ? props.component
                      : props.editComponent
                  }
                  answerModified={isAnswerModified}
                  lessonState={
                    portfolio?.state
                  } /* Passing lessonState instead of using LessonPlayerContext consumer in the submissionPanel component.
                        This is because the ApiPortfolioState will be updated when the learner submits the lesson from 'work required' state.
                        At that time, the feedback should not be shown */
                />
              )}
            {LessonComponentService.canSubmit(mode) &&
              (LessonComponentService.isNonAutoMarking(props.component) ||
                isSurvey) && (
                <NonAutoMarkingSubmissionPanel
                  points={points}
                  assignedPoints={assignedPoints}
                  component={
                    finalized || quizLocked
                      ? props.component
                      : props.editComponent
                  }
                />
              )}
          </Disable>
        </Grid>
      </LessonComponentContextProvider>
    </div>
  );
}
